// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file DropdownMenuRadioItem.tsx
 * @input React, stylex, Item + Icon + DropdownMenu context + tokens from core
 * @output DropdownMenuRadioItem — a single option in a radio group.
 * @position Sub-component; must be used inside a DropdownMenuRadioGroup.
 *
 * A menu item representing one option in a single-select group
 * (role="menuitemradio"). The row owns the role and aria-checked; there is no
 * nested native <input> (per the WAI-ARIA menuitemradio pattern). Selection
 * state + onChange come from DropdownMenuRadioGroupContext. Keyboard nav +
 * Enter/Space activation come from the parent DropdownMenu.
 *
 * The round radio visual (circle + center dot) is decorative (aria-hidden) —
 * the row owns the checked state. Its size is derived from the menu's item size
 * and it swaps to the inline-end of the row on coarse-pointer (touch) devices
 * via CSS `order`. The dot is a shaped element, not an icon (a filled circle
 * has no registry glyph).
 */
import { useCallback } from 'react';
import * as stylex from '@stylexjs/stylex';
import { renderIconSlot } from "../Icon/index.js";
import { Item } from "../Item/index.js";
import { useDropdownMenuContext } from "./DropdownMenuContext.js";
import { focusMenuItemOnHover } from "./menuItemHover.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, durationVars, easeVars, borderVars } from "../theme/tokens.stylex.js";
import { mergeProps, themeProps } from "../utils/index.js";
import { useDropdownMenuRadioGroupContext } from "./DropdownMenuContext.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  root: {
    kzqmXN: "xh8yej3",
    kaIpWk: "x1ws5lxm",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w x1c52tdz",
    kkrTdU: "x1ypdohk",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  disabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  circle: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kB7OPa: "x9f619",
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kaIpWk: "x16rqkct",
    k1ekBW: "xts7igz",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    kayTVb: "x1g77sc7 xozvky",
    keTefX: "x1lziwak x1jymrmb",
    $$css: true
  },
  unchecked: {
    kVAM5u: "xvy26l8",
    kWkggS: "x10xzikg",
    $$css: true
  },
  checked: {
    kVAM5u: "xad5do",
    kWkggS: "x1ewilqj",
    $$css: true
  },
  dot: {
    kaIpWk: "x16rqkct",
    kWkggS: "x1azo05",
    $$css: true
  }
};
const circleSizeStyles = {
  sm: {
    kzqmXN: "x1xp8n7a",
    kZKoxP: "xmix8c7",
    $$css: true
  },
  md: {
    kzqmXN: "x17z2i9w",
    kZKoxP: "x17rw0jw",
    $$css: true
  }
};
const dotSizeStyles = {
  sm: {
    kzqmXN: "x1v4s8kt",
    kZKoxP: "xols6we",
    $$css: true
  },
  md: {
    kzqmXN: "x1xc55vz",
    kZKoxP: "xdk7pt",
    $$css: true
  }
};
/**
 * A single option in a DropdownMenuRadioGroup (role="menuitemradio").
 *
 * @example
 * ```
 * <DropdownMenuRadioGroup value={sort} onChange={setSort} label="Sort by">
 *   <DropdownMenuRadioItem value="newest" label="Newest" />
 *   <DropdownMenuRadioItem value="oldest" label="Oldest" icon="clock" />
 * </DropdownMenuRadioGroup>
 * ```
 */
export function DropdownMenuRadioItem({
  value,
  label,
  description,
  icon,
  isDisabled = false,
  endContent,
  xstyle,
  className,
  style,
  ...rest
}) {
  const menuCtx = useDropdownMenuContext();
  const groupCtx = useDropdownMenuRadioGroupContext();
  if (!groupCtx) {
    throw new Error('DropdownMenuRadioItem must be used within a DropdownMenuRadioGroup');
  }
  const menuSize = menuCtx?.menuSize ?? 'md';
  const controlSize = menuSize === 'sm' ? 'sm' : 'md';
  const isChecked = groupCtx.value === value;
  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }
    groupCtx.onChange(value);
    if (groupCtx.hasCloseOnSelect) {
      menuCtx?.closeMenu();
    }
  }, [isDisabled, groupCtx, value, menuCtx]);
  const handlePointerMove = useCallback(e => focusMenuItemOnHover(e, isDisabled), [isDisabled]);
  return /*#__PURE__*/_jsx(Item, {
    ...rest,
    role: "menuitemradio",
    "aria-checked": isChecked,
    tabIndex: isDisabled ? undefined : -1,
    onPointerMove: handlePointerMove,
    marker: /*#__PURE__*/_jsx("span", {
      "aria-hidden": "true",
      ...mergeProps(themeProps('dropdown-menu-radio', {
        size: controlSize,
        checked: isChecked ? 'checked' : null,
        disabled: isDisabled ? 'disabled' : null
      }), stylex.props(styles.circle, circleSizeStyles[controlSize], isChecked ? styles.checked : styles.unchecked)),
      children: isChecked && /*#__PURE__*/_jsx("span", {
        ...mergeProps(themeProps('dropdown-menu-radio-dot', {
          size: controlSize,
          checked: 'checked',
          disabled: isDisabled ? 'disabled' : null
        }), stylex.props(styles.dot, dotSizeStyles[controlSize]))
      })
    }),
    startContent: icon ? renderIconSlot(icon, {
      size: 'sm',
      color: 'secondary'
    }) : undefined,
    label: label,
    description: description,
    endContent: endContent,
    onClick: handleClick,
    isDisabled: isDisabled,
    xstyle: [styles.root, isDisabled && styles.disabled, xstyle],
    ...mergeProps(themeProps('dropdown-menu-item', {
      size: menuSize
    }), {
      className,
      style
    })
  });
}
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';